/**
 * /os - OS engagement owner status view (PRD-004 Batch 2 + Batch 3, owner
 * surface).
 *
 * The index child of the /os layout route. Renders the OS status surface
 * (OsStatusComponent, PR-OS-02) for the owner's engagement, and - once the
 * engagement is at or past handoff_ready AND a handoff_deliverable_id is set -
 * the handoff view (OsHandoffView, Batch 3: always-on walkthrough video +
 * README + mental model + downloads) below it. The engagement is resolved by
 * the parent guard (lib/os-guard.ts) and read from route context, so this view
 * does not re-fetch it; the handoff deliverable + files ARE fetched here on
 * mount (they only exist past handoff_ready and the status line does not need
 * them).
 *
 * Read-only (scoping pass §9): there is no owner control that mutates the
 * lifecycle. Downloads mint signed URLs (storage RLS gates access) but write
 * nothing.
 *
 * Operator case: an operator may reach /os to QA the render (os-guard returns
 * isOperator with a null engagement, since operators have no subscriber row of
 * their own). We surface a small QA note rather than a blank or a fabricated
 * engagement, degrading honestly.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { OsStatusComponent } from '@/components/os/OsStatusComponent';
import { OsHandoffView } from '@/components/os/OsHandoffView';
import {
  OsInputRequestCard,
  fetchOsInputRequest,
  type RecipientInputRequest,
} from '@/components/input-requests';
import {
  fetchOwnHandoffDeliverable,
  fetchOwnHandoffFiles,
  isAtOrPastHandoffReady,
  type OwnHandoffDeliverable,
  type OwnHandoffFile,
  type OwnOsEngagement,
} from '@/lib/os';

export const Route = createFileRoute('/os/')({
  component: OsStatusView,
});

interface HandoffData {
  deliverable: OwnHandoffDeliverable;
  files: OwnHandoffFile[];
}

function OsHandoffSection({
  deliverableId,
  showConversion,
}: {
  deliverableId: string;
  showConversion: boolean;
}) {
  const [data, setData] = useState<HandoffData | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const deliverable = await fetchOwnHandoffDeliverable(deliverableId);
        if (!deliverable) return;
        const files = await fetchOwnHandoffFiles(deliverableId);
        if (cancelled) return;
        setData({ deliverable, files });
      } catch {
        // Read-only surface: a failed handoff read degrades to the status line
        // only rather than throwing the whole /os page. The owner still sees
        // where the engagement stands.
        if (!cancelled) setData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deliverableId]);

  if (!data) return null;
  return (
    <OsHandoffView
      deliverable={data.deliverable}
      files={data.files}
      showConversion={showConversion}
    />
  );
}

/**
 * Surface B (PRD-005 / ARCH-028, IR-T-08): the OS owner input-request card,
 * sat in the owner status surface above OsStatusComponent. Reads the single OS
 * request for this engagement (RLS scopes to the owner's own rows). Empty =
 * the card is absent (OsInputRequestCard returns null), so the status surface
 * is unchanged. A failed read degrades to no card, like the handoff section.
 */
function OsInputRequestSection({ engagementId }: { engagementId: string }) {
  const [request, setRequest] = useState<RecipientInputRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const row = await fetchOsInputRequest(engagementId);
        if (!cancelled) setRequest(row);
      } catch {
        if (!cancelled) setRequest(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  return <OsInputRequestCard request={request} />;
}

function OsEngagementView({ engagement }: { engagement: OwnOsEngagement }) {
  const showHandoff =
    isAtOrPastHandoffReady(engagement.status) &&
    engagement.handoff_deliverable_id !== null;

  return (
    <div>
      <OsInputRequestSection engagementId={engagement.id} />
      <OsStatusComponent engagement={engagement} />
      {showHandoff && engagement.handoff_deliverable_id ? (
        <OsHandoffSection
          deliverableId={engagement.handoff_deliverable_id}
          showConversion={engagement.status === 'open'}
        />
      ) : null}
    </div>
  );
}

function OsStatusView() {
  const { os } = Route.useRouteContext();

  if (!os.engagement) {
    // Operator QA path: no engagement of their own to show.
    return (
      <div
        className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-linen)] p-6 text-sm text-[color:var(--color-text-mute)]"
        role="note"
      >
        Operator view: no OS engagement is attached to this account. Open a
        specific engagement from the dashboard to drive its lifecycle.
      </div>
    );
  }

  return <OsEngagementView engagement={os.engagement} />;
}
